import type { GroupBase, StylesConfig } from 'react-select';

import { getRgbaValue } from '../../tokens/colors';
import { rem } from '../../tokens/spacing';
import type { Theme } from '../../theme/types';

export interface CreateWealthlaneReactSelectStylesOptions {
  hasError?: boolean;
  /** Optional fixed width for the control (legacy `width` prop); default full width. */
  controlWidth?: string;
}

function cv(theme: Theme, key: string, fallback: string): string {
  return theme.cssVars[key] ?? fallback;
}

/**
 * `react-select` styles aligned with legacy `formSelect.tsx`, driven by {@link Theme} tokens
 * so light / dark / high-contrast and brand colors track `ThemeProvider`.
 */
export function createWealthlaneReactSelectStyles<Option, IsMulti extends boolean>(
  theme: Theme,
  opts: CreateWealthlaneReactSelectStylesOptions = {},
): StylesConfig<Option, IsMulti, GroupBase<Option>> {
  const surface = cv(theme, '--ui-surface', '#ffffff');
  const text = cv(theme, '--ui-text', '#161616');
  const textSubtle = cv(theme, '--ui-text-subtle', '#767676');
  const textMuted = cv(theme, '--ui-text-muted', '#595959');
  const border = cv(theme, '--ui-input-border', cv(theme, '--ui-border', '#d9d9d9'));
  const borderFocus = cv(theme, '--ui-input-border-focus', '#1a73e8');
  const interactive = cv(theme, '--ui-interactive', '#6b4d9a');
  /** Focused option label color — matches legacy `formSelect` (blue accent). */
  const optionFocusColor = cv(theme, '--ui-input-border-focus', interactive);
  const danger = cv(theme, '--ui-status-danger', '#d32f2f');
  const shadow = cv(theme, '--ui-input-shadow', 'rgba(0,0,0,0.12)');
  const inset = cv(theme, '--ui-background-muted', '#f7f7f7');
  const focusOptionBg = getRgbaValue('#f3f3f3', 0.85);

  const controlShadow = `0 ${rem(2)} ${rem(4)} ${shadow}`;
  const menuShadow = `0 ${rem(2)} ${rem(4)} ${shadow}`;

  return {
    control: (styles, state) => ({
      ...styles,
      borderRadius: `var(--ui-radius-md, ${rem(6)})`,
      fontWeight: 400,
      fontSize: rem(15),
      minHeight: rem(40),
      height: 'auto',
      transition: 'all 0.2s linear',
      border:
        opts.hasError === true
          ? `${rem(1)} solid ${danger} !important`
          : state.isFocused
            ? `${rem(1)} solid ${borderFocus} !important`
            : `${rem(1)} solid ${border} !important`,
      boxShadow: controlShadow,
      width: opts.controlWidth ?? '100%',
      maxWidth: '100%',
      backgroundColor: surface,
      color: text,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      ':hover': {
        border: `${rem(1)} solid ${borderFocus} !important`,
      },
    }),
    valueContainer: (styles, state) => ({
      ...styles,
      minHeight: rem(38),
      height: state.hasValue ? 'auto' : rem(38),
      paddingLeft: rem(16),
      paddingRight: rem(5),
      gap: rem(4),
    }),
    singleValue: (styles) => ({
      ...styles,
      color: text,
    }),
    placeholder: (styles, state) => ({
      ...styles,
      padding: 0,
      color: state.isFocused ? textSubtle : textSubtle,
      whiteSpace: 'nowrap',
    }),
    input: (styles) => ({
      ...styles,
      color: text,
    }),
    menu: (styles) => ({
      ...styles,
      background: 'transparent',
      boxShadow: 'none',
      zIndex: 9999,
    }),
    menuPortal: (styles) => ({
      ...styles,
      zIndex: 9999,
    }),
    menuList: (styles) => ({
      ...styles,
      maxHeight: rem(256),
      fontSize: rem(14),
      padding: rem(10),
      borderRadius: `var(--ui-radius-sm, ${rem(4)})`,
      width: 'fit-content',
      minWidth: '100%',
      background: surface,
      border: `${rem(1)} solid ${border} !important`,
      boxShadow: menuShadow,
      '::-webkit-scrollbar': {
        width: rem(5),
      },
      '::-webkit-scrollbar-track': {
        background: inset,
        borderRadius: `var(--ui-radius-md, ${rem(7)})`,
      },
      '::-webkit-scrollbar-thumb': {
        background: textMuted,
        borderRadius: `var(--ui-radius-md, ${rem(7)})`,
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: text,
      },
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      height: rem(38),
      minHeight: rem(38),
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: 'none',
    }),
    option: (styles, state) => ({
      ...styles,
      backgroundColor: state.isFocused ? focusOptionBg : undefined,
      color: state.isDisabled ? textSubtle : state.isFocused ? optionFocusColor : text,
      fontWeight: state.isFocused && !state.isDisabled ? 500 : 400,
      borderRadius: `var(--ui-radius-sm, ${rem(4)})`,
      wordBreak: 'break-all',
      whiteSpace: 'normal',
      maxWidth: rem(600),
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
    group: (styles) => ({
      ...styles,
      paddingTop: rem(4),
    }),
    groupHeading: (styles) => ({
      ...styles,
      fontSize: rem(12),
      fontWeight: 600,
      color: textMuted,
      padding: `${rem(6)} ${rem(12)}`,
      margin: 0,
      textTransform: 'none',
    }),
    multiValue: (styles) => ({
      ...styles,
      marginBottom: 0,
      marginTop: 0,
      marginLeft: 0,
      borderRadius: `var(--ui-radius-sm, ${rem(4)})`,
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      fontSize: rem(16),
      lineHeight: rem(20),
      padding: `${rem(6)} ${rem(4)}`,
    }),
    loadingIndicator: (styles) => ({
      ...styles,
      color: interactive,
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      color: textMuted,
      fontSize: rem(14),
    }),
    loadingMessage: (styles) => ({
      ...styles,
      color: textMuted,
      fontSize: rem(14),
    }),
  };
}
